/**
 * Market Breakdown Section
 * @see Sprint 28 - US-28.3
 */

import { Card, CardHeader, CardTitle, CardDescription, CardContent, Skeleton } from '@/components/ui';
import { useCanSeeFinancials, useCurrency } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import { useMarketBreakdown } from '../hooks';

const MARKET_CONFIG = [
  {
    key: 'FOOD' as const,
    label: 'Food',
    subtitle: 'Food cost & denrees',
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    textMuted: 'text-green-600',
  },
  {
    key: 'NON_FOOD' as const,
    label: 'Non-Food',
    subtitle: 'Consommables & emballages',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    textMuted: 'text-blue-600',
  },
  {
    key: 'MAINTENANCE' as const,
    label: 'Maintenance',
    subtitle: 'Entretien & hygiene',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-700',
    textMuted: 'text-gray-600',
  },
];

export function MarketBreakdown() {
  const canSeeFinancials = useCanSeeFinancials();
  const currency = useCurrency();
  const { data, isLoading, isError } = useMarketBreakdown();

  if (!canSeeFinancials) return null;
  if (isError) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Repartition par marche</CardTitle>
        <CardDescription>Valorisation du stock par type de marche</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {MARKET_CONFIG.map((market) => {
              const value = data?.[market.key] ?? 0;
              const total = data?.total ?? 0;
              const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';

              return (
                <div
                  key={market.key}
                  className={`rounded-lg border p-4 ${market.bg} ${market.border}`}
                >
                  <div className={`text-sm font-medium ${market.text}`}>
                    {market.label}
                  </div>
                  <div className={`text-2xl font-bold ${market.text} mt-1`}>
                    {pct}%
                  </div>
                  <div className={`text-sm ${market.textMuted} mt-1`}>
                    {formatCentimes(value, currency)}
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">
                    {market.subtitle}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
